import { Field } from 'module/api/field/types';
import { FieldType } from 'module/types/filter-field/field/field';
import { FieldFilter } from 'module/types/filter-field/filter';
import { BooleanFilterOperationType } from 'module/types/filter-field/filter/boolean';
import { DateFieldFilter } from 'module/types/filter-field/filter/date';
import { NumberFilterOperationType } from 'module/types/filter-field/filter/number';
import { SelectFilterOperationType } from 'module/types/filter-field/filter/select';
import { TextFieldFilterOperationType } from 'module/types/filter-field/filter/text';
import { format } from 'module/ui/components/molecules/field-filter/criteria/date/format';
import {
  IntegrationFieldType,
  filterableFieldType,
} from 'module/utils/integrations/get-field';
import { Tag } from '@rocketlane/ui';
import { Typography, Space } from 'antd';
import { snakeCase } from 'lodash';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  booleanTriggerOptionRenderers,
  numberTriggerOptionRenderers,
  textTriggerOptionRenderers,
  dateTriggerOptionRenderers,
  selectTriggerOptionRenderers,
} from '../custom-trigger-options';

const StyledSpace = styled(Space)`
  .ant-space-align-center {
    align-items: center;
  }
`;

const { Text } = Typography;

const OperatorText = styled(Text)`
  white-space: nowrap;
  flex-shrink: 0;
`;

const StyledEllipsisSpan = styled.span`
  display: inline-block;
  max-width: var(--width-md);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledEllipsisText = styled(Text)`
  max-width: var(--width-xl);
`;

const MultiSelectValueContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  min-width: 0;
`;

const MultiSelectTagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-02);
  min-width: 0;
`;

type ValueRendererProps = {
  selected: FieldFilter;
  customOptionTrigger?: boolean;
};

export const ValueRenderer = ({
  selected,
  customOptionTrigger = false,
}: ValueRendererProps) => {
  const { field } = selected;

  // TODO: @krishna or @mohmmed This is because FieldBase dosent seem to have the below on types, but in actual it contains. Its better to extend FieldBase from Field.
  const selectedField = {
    fieldType: (field as unknown as Field).fieldType as FieldType,
    integrationFieldId: (field as unknown as Field).fieldId.toString(),
    fieldDataType: (field as unknown as Field).fieldDataType,
    optionsAvailable: (field as unknown as Field).optionsAvailable,
  };

  switch (filterableFieldType(selectedField as any)) {
    case IntegrationFieldType.SingleSelect:
    case IntegrationFieldType.MultiSelect: {
      if (
        [
          SelectFilterOperationType.IsOneOf,
          SelectFilterOperationType.IsNoneOf,
        ].includes(selected.type as SelectFilterOperationType)
      ) {
        return (
          <MultiSelectValueContainer>
            <OperatorText>
              {customOptionTrigger ? (
                selectTriggerOptionRenderers(
                  selected.type as SelectFilterOperationType,
                )
              ) : (
                <FormattedMessage
                  id={`task_list.filters.${snakeCase(
                    SelectFilterOperationType[selected.type],
                  )}`}
                />
              )}
            </OperatorText>
            <MultiSelectTagList>
              {(selected as any)?.value?.map((each) => (
                <Tag variant="basic" color="cyan" key={each}>
                  <StyledEllipsisSpan title={each}>
                    {each}
                  </StyledEllipsisSpan>
                </Tag>
              ))}
            </MultiSelectTagList>
          </MultiSelectValueContainer>
        );
      }

      return (
        <Space>
          <OperatorText>
            {customOptionTrigger ? (
              selectTriggerOptionRenderers(
                selected.type as SelectFilterOperationType,
              )
            ) : (
              <FormattedMessage
                id={`task_list.filters.${snakeCase(
                  SelectFilterOperationType[selected.type],
                )}`}
              />
            )}
          </OperatorText>
          {[
            SelectFilterOperationType.IsEmpty,
            SelectFilterOperationType.IsNotEmpty,
          ].includes(selected.type as SelectFilterOperationType) ? null : (
            <Tag variant="basic" color="cyan">
              <StyledEllipsisSpan title={(selected as any)?.value}>
                {(selected as any)?.value}
              </StyledEllipsisSpan>
            </Tag>
          )}
        </Space>
      );
    }

    case IntegrationFieldType.Date: {
      return format(selected as DateFieldFilter, dateTriggerOptionRenderers);
    }

    case IntegrationFieldType.TextArea:
    case IntegrationFieldType.Text: {
      return (
        <StyledSpace>
          <OperatorText>
            {customOptionTrigger ? (
              textTriggerOptionRenderers(
                selected.type as TextFieldFilterOperationType,
              )
            ) : (
              <FormattedMessage
                id={`settings.integrations.sf.trigger.option.${snakeCase(
                  TextFieldFilterOperationType[selected.type],
                )}`}
              />
            )}
          </OperatorText>
          {[
            TextFieldFilterOperationType.IsEmpty,
            TextFieldFilterOperationType.IsNotEmpty,
          ].includes(selected.type as TextFieldFilterOperationType) ? null : (
            <StyledEllipsisText
              strong
              ellipsis={{ tooltip: true }}
            >
              {(selected as any)?.value}
            </StyledEllipsisText>
          )}
        </StyledSpace>
      );
    }

    case IntegrationFieldType.Number: {
      return (
        <Space>
          <OperatorText>
            {customOptionTrigger ? (
              numberTriggerOptionRenderers(
                selected.type as NumberFilterOperationType,
              )
            ) : (
              <FormattedMessage
                id={`settings.integrations.sf.trigger.option.${snakeCase(
                  NumberFilterOperationType[selected.type],
                )}`}
              />
            )}
          </OperatorText>
          {[
            NumberFilterOperationType.IsEmpty,
            NumberFilterOperationType.IsNotEmpty,
          ].includes(selected.type as NumberFilterOperationType) ? null : (
            <StyledEllipsisText
              strong
              ellipsis={{ tooltip: true }}
            >
              {(selected as any)?.value}
            </StyledEllipsisText>
          )}
        </Space>
      );
    }

    case IntegrationFieldType.Boolean: {
      return (
        <Space>
          <OperatorText>
            {customOptionTrigger ? (
              booleanTriggerOptionRenderers(
                selected.type as BooleanFilterOperationType,
              )
            ) : (
              <FormattedMessage
                id={`settings.integrations.sf.trigger.option.${snakeCase(
                  BooleanFilterOperationType[selected.type],
                )}`}
              />
            )}
          </OperatorText>
          {[
            BooleanFilterOperationType.IsEmpty,
            BooleanFilterOperationType.IsNotEmpty,
          ].includes(selected.type as BooleanFilterOperationType) ? null : (
            <StyledEllipsisText
              strong
              ellipsis={{ tooltip: true }}
            >
              {(selected as any)?.value}
            </StyledEllipsisText>
          )}
        </Space>
      );
    }
    default: {
      return (
        <StyledEllipsisText
          strong
          ellipsis={{ tooltip: true }}
        >
          {(selected as any)?.value}
        </StyledEllipsisText>
      );
    }
  }
};
